<script lang="ts" setup>
import { computed, ref, toRef } from "vue";
import {
  useProjectVersionDiff,
  useAcceptProductVersionUpgrade,
  useAcceptWorkflowUpgrade,
  type WorkflowDiffEntryView,
} from "@/queries/projects";

interface Props {
  projectId: string;
  enabled: boolean;
}

const props = defineProps<Props>();
const projectIdRef = toRef(props, "projectId");
const enabledRef = toRef(props, "enabled");

const { versionDiff, versionDiffLoading } = useProjectVersionDiff(projectIdRef, enabledRef);
const { acceptUpgradeAsync, acceptUpgradePending, acceptUpgradeError } =
  useAcceptProductVersionUpgrade(projectIdRef);
const { acceptWorkflowUpgradeAsync, acceptWorkflowUpgradeError } =
  useAcceptWorkflowUpgrade(projectIdRef);

const showModal = ref(false);
// Track which specific workflow row is currently being processed so we can show a per-row spinner
// without blocking other rows.
const inFlightWorkflowId = ref<string | null>(null);

const isUpgradeAvailable = computed(
  () => !!versionDiff.value && versionDiff.value.isUpgradeAvailable
);

const targetVersionLabel = computed(
  () => versionDiff.value?.targetVersionNumber ?? ""
);

// Group workflows by status so the modal can show clear sections.
const grouped = computed(() => {
  const result = {
    changed: [] as WorkflowDiffEntryView[],
    added: [] as WorkflowDiffEntryView[],
    removed: [] as WorkflowDiffEntryView[],
  };
  if (!versionDiff.value) return result;
  for (const entry of versionDiff.value.workflows) {
    if (entry.status === "Changed") result.changed.push(entry);
    else if (entry.status === "Added") result.added.push(entry);
    else if (entry.status === "Removed") result.removed.push(entry);
  }
  return result;
});

// Workflows where the customer has a customization that diverges from the publisher's current
// version. These are the only rows where per-workflow accept is meaningful — Added/Removed/Unchanged
// don't have a "your customization" decision to make.
const customizedAndDivergent = computed(() =>
  (versionDiff.value?.workflows ?? []).filter(
    (e) => e.isCurrentlyCustomized && e.customizationLostOnAccept
  )
);

// Top-level summary count for the banner — number of customized workflows that have an update.
const customizedWithUpdates = computed(() => customizedAndDivergent.value.length);

// Total count of changes the customer would see if they accepted everything (purely informational).
const totalChanges = computed(
  () => grouped.value.changed.length + grouped.value.added.length + grouped.value.removed.length
);

function open() {
  showModal.value = true;
}

function close() {
  if (acceptUpgradePending.value || inFlightWorkflowId.value) return;
  showModal.value = false;
}

async function acceptOne(workflowId: string) {
  inFlightWorkflowId.value = workflowId;
  try {
    await acceptWorkflowUpgradeAsync(workflowId);
  } catch {
    // surfaced via acceptWorkflowUpgradeError
  } finally {
    inFlightWorkflowId.value = null;
  }
}

async function acceptAll() {
  if (!versionDiff.value) return;
  try {
    await acceptUpgradeAsync(versionDiff.value.targetProductVersionId);
    showModal.value = false;
  } catch {
    // surfaced via acceptUpgradeError
  }
}
</script>

<template>
  <div v-if="versionDiffLoading" class="d-none"></div>

  <div
    v-else-if="isUpgradeAvailable"
    class="alert alert-info d-flex align-items-center justify-content-between mb-3"
    role="alert"
  >
    <div>
      <i class="bi bi-arrow-up-circle me-2"></i>
      <strong>Updates available</strong>
      <span v-if="customizedWithUpdates > 0">
        — {{ customizedWithUpdates }} customized
        workflow{{ customizedWithUpdates === 1 ? "" : "s" }}
        {{ customizedWithUpdates === 1 ? "has" : "have" }} a publisher update.
      </span>
      <span v-else>
        — Product version <strong>{{ targetVersionLabel }}</strong> has changes you haven't accepted yet.
      </span>
    </div>
    <button class="bb-btn-primary-cta btn-sm" @click="open">
      <i class="bi bi-eye me-1"></i> Preview
    </button>
  </div>

  <!-- Diff Modal -->
  <div
    v-if="showModal"
    class="modal fade show d-block"
    tabindex="-1"
    @click="close"
  >
    <div class="modal-dialog modal-lg" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h2 class="modal-title mb-0">
              Updates for product version {{ targetVersionLabel }}
            </h2>
            <small class="text-muted">
              Accept individual workflow updates, or accept everything at once.
            </small>
          </div>
          <button
            type="button"
            class="btn-close"
            :disabled="acceptUpgradePending || !!inFlightWorkflowId"
            @click="close"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Changed workflows — per-workflow accept available for customized rows -->
          <section v-if="grouped.changed.length > 0" class="mb-3">
            <h4 class="mb-2">
              <i class="bi bi-arrow-repeat me-2"></i>
              Changed
              <span class="badge bg-secondary">{{ grouped.changed.length }}</span>
            </h4>
            <ul class="list-group">
              <li
                v-for="entry in grouped.changed"
                :key="entry.workflowId"
                class="list-group-item"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-medium">
                      {{ entry.workflowName }}
                      <span
                        v-if="entry.isCurrentlyCustomized"
                        class="badge bg-info text-dark ms-2"
                        style="font-size: 0.7rem"
                      >Customized</span>
                    </div>
                    <small class="text-muted">
                      <span v-if="entry.isCurrentlyCustomized">
                        Your {{ entry.currentVersionLabel ?? "(none)" }}
                      </span>
                      <span v-else>
                        {{ entry.currentVersionLabel ?? "(none)" }}
                      </span>
                      →
                      Publisher's {{ entry.targetVersionLabel ?? "(none)" }}
                    </small>
                  </div>
                  <button
                    v-if="entry.isCurrentlyCustomized"
                    class="bb-btn-secondary-cta btn-sm"
                    :disabled="!!inFlightWorkflowId || acceptUpgradePending"
                    :title="`Discard your customization for '${entry.workflowName}' and use the publisher's ${entry.targetVersionLabel} version`"
                    @click="acceptOne(entry.workflowId)"
                  >
                    <span
                      v-if="inFlightWorkflowId === entry.workflowId"
                      class="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>
                    <i v-else class="bi bi-check-circle me-1"></i>
                    Accept update
                  </button>
                  <small v-else class="text-muted fst-italic">automatic</small>
                </div>

                <!-- Phase 1b: wizard field overwrites. Shows what wizard answers will change
                     on accept — Overwrite policy + value differs, or Preserve policy seeding
                     a previously-empty field. -->
                <div
                  v-if="entry.wizardFieldOverwrites.length > 0"
                  class="mt-2 ps-3 border-start border-2 border-warning"
                >
                  <small class="text-muted d-block mb-1">
                    <i class="bi bi-pencil-square me-1"></i>
                    Wizard fields that will change:
                  </small>
                  <ul class="list-unstyled mb-0">
                    <li
                      v-for="overwrite in entry.wizardFieldOverwrites"
                      :key="`${overwrite.stepId}:${overwrite.fieldName}`"
                      class="small"
                    >
                      <span class="fw-medium">{{ overwrite.stepName }}</span>
                      <span class="text-muted"> · </span>
                      <span class="text-muted">{{ overwrite.fieldName }}</span>
                      <span class="text-muted">:</span>
                      <code class="ms-1">{{ overwrite.currentValue ?? "(empty)" }}</code>
                      <span class="text-muted mx-1">→</span>
                      <code>{{ overwrite.newValue ?? "(empty)" }}</code>
                      <span
                        v-if="overwrite.policy === 'Overwrite'"
                        class="badge bg-warning text-dark ms-2"
                        style="font-size: 0.65rem"
                        title="Publisher has marked this field as forced — the new value applies regardless of any value you had."
                      >forced</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </section>

          <!-- Added workflows — informational, no per-row accept (auto-included via product version) -->
          <section v-if="grouped.added.length > 0" class="mb-3">
            <h4 class="mb-2">
              <i class="bi bi-plus-circle me-2"></i>
              Added
              <span class="badge bg-secondary">{{ grouped.added.length }}</span>
            </h4>
            <ul class="list-group">
              <li
                v-for="entry in grouped.added"
                :key="entry.workflowId"
                class="list-group-item"
              >
                <div class="fw-medium">{{ entry.workflowName }}</div>
                <small class="text-muted">
                  Publisher's {{ entry.targetVersionLabel ?? "" }}
                </small>
              </li>
            </ul>
          </section>

          <!-- Removed workflows — accepting drops the customer's customization AND the workflow itself -->
          <section v-if="grouped.removed.length > 0" class="mb-3">
            <h4 class="mb-2">
              <i class="bi bi-dash-circle me-2"></i>
              Removed
              <span class="badge bg-secondary">{{ grouped.removed.length }}</span>
            </h4>
            <ul class="list-group">
              <li
                v-for="entry in grouped.removed"
                :key="entry.workflowId"
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <div class="fw-medium">
                    {{ entry.workflowName }}
                    <span
                      v-if="entry.isCurrentlyCustomized"
                      class="badge bg-info text-dark ms-2"
                      style="font-size: 0.7rem"
                    >Customized</span>
                  </div>
                  <small class="text-muted">
                    <span v-if="entry.isCurrentlyCustomized">
                      Your {{ entry.currentVersionLabel ?? "" }}
                    </span>
                    <span v-else>
                      {{ entry.currentVersionLabel ?? "" }}
                    </span>
                    — removed by publisher
                  </small>
                </div>
                <button
                  v-if="entry.isCurrentlyCustomized"
                  class="bb-btn-secondary-cta btn-sm"
                  :disabled="!!inFlightWorkflowId || acceptUpgradePending"
                  :title="`Drop '${entry.workflowName}' from your project (the publisher removed it from this product version)`"
                  @click="acceptOne(entry.workflowId)"
                >
                  <span
                    v-if="inFlightWorkflowId === entry.workflowId"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  <i v-else class="bi bi-trash me-1"></i>
                  Drop workflow
                </button>
              </li>
            </ul>
          </section>

          <div v-if="totalChanges === 0" class="text-muted">
            No workflow-level differences detected.
          </div>

          <div v-if="acceptWorkflowUpgradeError" class="alert alert-danger mt-3">
            <i class="bi bi-exclamation-circle me-2"></i>
            Failed to accept update.
            <span v-if="acceptWorkflowUpgradeError.message"> {{ acceptWorkflowUpgradeError.message }}</span>
          </div>

          <div v-if="acceptUpgradeError" class="alert alert-danger mt-3">
            <i class="bi bi-exclamation-circle me-2"></i>
            Failed to accept upgrade.
            <span v-if="acceptUpgradeError.message"> {{ acceptUpgradeError.message }}</span>
          </div>
        </div>

        <div class="modal-footer d-flex justify-content-between align-items-center">
          <small class="text-muted" v-if="customizedWithUpdates > 0">
            {{ customizedWithUpdates }} customization{{ customizedWithUpdates === 1 ? "" : "s" }}
            would be discarded if you accept everything.
          </small>
          <span v-else></span>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="bb-btn-secondary-cta"
              :disabled="acceptUpgradePending || !!inFlightWorkflowId"
              @click="close"
            >Close</button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              :disabled="acceptUpgradePending || !!inFlightWorkflowId || totalChanges === 0"
              :title="customizedWithUpdates > 0
                ? 'Discard ALL customizations and accept every update'
                : 'Accept all updates'"
              @click="acceptAll"
            >
              <span
                v-if="acceptUpgradePending"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              <i v-else class="bi bi-check-circle me-1"></i>
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
